import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { Auth } from 'aws-amplify'
import Link from 'next/link'
import { parseCookies } from 'nookies'
import { useForm } from 'react-hook-form'
import {
  Breadcrumb,
  Button,
  Col,
  ControlledForm,
  ControlledInput,
  Icon,
  Link as LinkComp,
  Row,
  Text
} from '@/client/components'
import { useAuthUser, useRouterPush, useWindowSize } from '@/client/hooks'
import { Cookies } from '@/client/types'
import { getPreviousPath, getSafeRedirect, ROUTER_PATH } from '@/client/utils'
import { addCartItem, getCartItems, getUserCart, updateCart } from '@/infra/services'
import { useI18n } from '@/locales'
import { schemaResolver } from './asserts'
import * as S from './styles'
import { FormCompProps, LoginFormSchema } from '../types'

// Pages a user should not return to after login (localized paths and internal page names)
const AUTH_PATHS = new Set([
  '/login',
  '/signup',
  '/forgot-password',
  '/activate-account',
  '/welcome',
  ...[
    ROUTER_PATH.LOGIN,
    ROUTER_PATH.SIGNUP,
    ROUTER_PATH.FORGOT_PASSWORD,
    ROUTER_PATH.SIGNUP_SUCCESS,
    ROUTER_PATH.CUSTOMER_REGISTERED
  ].flatMap((paths) => Object.values(paths))
])

/**
 * Check if a path (with optional locale prefix, query and trailing slash) is one of the auth pages.
 * @param path e.g. "/it/accesso/?foo=bar"
 * @param locales Locales of the shop, used to strip the locale prefix
 */
const isAuthPath = (path: string, locales: string[] = []) => {
  const segments = path.split(/[?#]/)[0].split('/').filter(Boolean)
  if (locales.includes(segments[0])) segments.shift()
  return AUTH_PATHS.has(`/${segments.join('/')}`)
}

/** See module:amazon-cognito-identity-js.ChallengeName */
const COGNITO_CHALLENGE_NEW_PASSWORD_REQUIRED = 'NEW_PASSWORD_REQUIRED'
const COGNITO_ERROR_USER_NOT_CONFIRMED = 'UserNotConfirmedException'
const COGNITO_ERROR_PASSWORD_RESET_REQUIRED = 'PasswordResetRequiredException'

export const Form: FC<FormCompProps> = ({ content }) => {
  const { breadcrumb, title, subtitle, aside, error_message } = content
  const { routerPush, defaultLocale, query, push, replace, locale, locales } = useRouterPush()
  const redirectRoute = getSafeRedirect(query.redirect)
  const windowSize = useWindowSize()
  const isLgBreakpoint = windowSize?.width > 768
  // On desktop the form has a dark blue background, red text is not readable there
  const errorColor = isLgBreakpoint ? 'warning-light' : 'danger'
  const successColor = isLgBreakpoint ? 'success' : 'normal'
  const [isLoadingForm, setIsLoadingForm] = useState<boolean>(false)
  const [formError, setFormError] = useState<string>('')
  const [showPassword, setShowPassword] = useState<boolean>(false)
  const [showNewPassword, setNewShowPassword] = useState<boolean>(false)
  const [showNewPasswordRepeat, setNewShowPasswordRepeat] = useState<boolean>(false)
  const [isForceNewPassword, setIsForceNewPassword] = useState<boolean>(false)
  const [user, setUser] = useState(null)
  const [formErrorCode, setFormErrorCode] = useState<string>('')
  const [unconfirmedEmail, setUnconfirmedEmail] = useState<string>('')
  const [resendState, setResendState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle')
  const t = useI18n()
  const { isAuthenticated, isUserLoading } = useAuthUser()

  const error_messages = {
    required_error: t('ASSERT_FIELD_REQUIRED'),
    email_error: t('ASSERT_INVALID_EMAIL'),
    password_error: t('ASSERT_INVALID_PASSWORD'),
    max_length_error: t('ASSERT_MAXIMUM_LENGTH'),
    min_length_error: t('ASSERT_MINIMUM_LENGTH'),
    password_match_error: t('ASSERT_BOTH_FIELDS_MUST_BE_EQUAL'),
    new_password_error: t('ASSERT_INVALID_PASSWORD')
  }

  const formMethods = useForm<LoginFormSchema>({
    defaultValues: { email: '', password: '' },
    resolver: schemaResolver(error_messages),
    mode: 'onBlur',
    reValidateMode: 'onBlur'
  })
  const { isDirty, isValid, isSubmitting } = formMethods.formState

  useEffect(() => {
    if (!isUserLoading && isAuthenticated) {
      routerPush(`${ROUTER_PATH.HOME[defaultLocale]}`)
    }
  }, [isAuthenticated, isUserLoading])

  const handleCart = useCallback(async () => {
    const cookies = parseCookies()

    // Get current cookies
    const cartId = cookies[Cookies.CART_ID]
    try {
      const res = await getUserCart(cartId)
      if (cartId && cartId !== res._id) {
        const { cart_items } = await getCartItems(cartId)
        for (const item of cart_items) {
          const { cart_item_selection } = item
          await addCartItem(cart_item_selection, locale, res._id)
        }

        // await addCartItem()
        return
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error)
    }

    // Get user Cart
    // Get cart Id from it
    // Check cookies cart is not equals userCart
    // Add update cart to current cart.

    if (cartId) {
      await updateCart(cartId, {})
      return
    }
  }, [locale])

  /**
   * Map a Cognito signIn error to a user facing message.
   * Wrong credentials and unknown users intentionally share the generic CMS text (no user enumeration).
   * @param code Cognito error code, e.g. "UserNotConfirmedException"
   * @param message Cognito error message
   */
  const getSignInErrorMessage = useCallback(
    (code: string, message: string) => {
      switch (code) {
        case COGNITO_ERROR_USER_NOT_CONFIRMED:
          return t('AWS_COGNITO_ERROR_USER_NOT_CONFIRMED')
        // Request rate of the whole user pool exceeded (API throttling)
        case 'TooManyRequestsException':
          return t('AWS_COGNITO_ERROR_TOO_MANY_REQUESTS')
        // Limit for this account exceeded
        case 'LimitExceededException':
          return t('AWS_COGNITO_ERROR_LIMIT_EXCEEDED')
        case 'NetworkError':
          return t('AWS_COGNITO_ERROR_NETWORK')
        case 'NotAuthorizedException':
          if (message.includes('Password attempts exceeded')) {
            return t('AWS_COGNITO_ERROR_ATTEMPT_LIMIT')
          }
          return error_message || ''
        default:
          return error_message || ''
      }
    },
    [t, error_message]
  )

  /**
   * Translate Cognito error message.
   * @param errormsg
   */
  const translateCognitoErrorMesage = useCallback(
    (errormsg: string) => {
      // Cognito may list several failed password rules at once, so show the whole policy instead
      if (/conform to policy|not long enough|(uppercase|lowercase|numeric|symbol) characters/.test(errormsg)) {
        return t('ASSERT_INVALID_PASSWORD')
      }
      if (errormsg.includes('Attempt limit exceeded')) {
        return t('AWS_COGNITO_ERROR_ATTEMPT_LIMIT')
      }
      return errormsg // No translation found
    },
    [t]
  )

  const handleResendConfirmation = useCallback(async () => {
    setResendState('sending')
    try {
      await Auth.resendSignUp(unconfirmedEmail)
      setResendState('sent')
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Resending confirmation email failed', error)
      setResendState('error')
    }
  }, [unconfirmedEmail])

  /**
   * Navigate after a successful login:
   * 1. to the redirect query parameter,
   * 2. back to the previous page of the shop (unless it is an auth page like signup),
   * 3. to the home page otherwise (e.g. the user came from another site or opened the login directly).
   */
  const navigateAfterLogin = useCallback(async () => {
    if (redirectRoute) {
      await replace(redirectRoute)
      return
    }

    const previousPath = getPreviousPath()
    if (previousPath && !isAuthPath(previousPath, locales)) {
      // The path already contains the locale prefix
      await replace(previousPath, undefined, { locale: false })
      return
    }

    await replace(ROUTER_PATH.HOME[defaultLocale])
  }, [redirectRoute, replace, locales, defaultLocale])

  const handleSubmitForm = useCallback(
    async (data: LoginFormSchema) => {
      //async (event: React.FormEvent<HTMLFormElement>) => {
      //event.preventDefault()
      setIsLoadingForm(true)
      const { email, password, new_password, confirm_password } = data

      // Case user is forced to set a new password before logging in.
      if (isForceNewPassword) {
        setIsLoadingForm(true)
        if (new_password && confirm_password && new_password === confirm_password) {
          await Auth.completeNewPassword(user, new_password)
            .then(async () => {
              setIsForceNewPassword(false)

              // REL-935 Catch all error as otherwise errors are interpreted as cognito errors
              try {
                await handleCart()
              } catch (error) {
                // eslint-disable-next-line no-console
                console.error('Cart merge failed', error)
              }

              await navigateAfterLogin()
            })
            .catch(async (reason) => {
              // The challenge session from signIn is only valid for a few minutes. Retrying with the
              // expired session always fails, so go back to the login form to start a new one.
              if (reason?.message?.includes('session is expired')) {
                setUser(null)
                setIsForceNewPassword(false)
                setFormError(t('AWS_COGNITO_ERROR_SESSION_EXPIRED'))
                return
              }

              // Error message from aws-amplify
              setFormError(translateCognitoErrorMesage(reason.message))
            })
            .finally(() => {
              setIsLoadingForm(false)
            })
        }
        return
      }

      // Normal login
      setFormError('')
      setFormErrorCode('')
      setResendState('idle')
      await Auth.signIn(email, password)
        .then(async (user) => {
          // User is forced to set a new password before logging in.
          if (user.challengeName === COGNITO_CHALLENGE_NEW_PASSWORD_REQUIRED) {
            // Email and password get disabled in the next step. A focused field that gets disabled fires a
            // blur event, and react-hook-form then reads its value as undefined ("Required" error).
            // So blur it while it is still enabled.
            ;(document.activeElement as HTMLElement | null)?.blur()
            setUser(user)
            setIsForceNewPassword(true)
            return
          }

          // Other challenges (e.g. MFA) are not supported by our user pools. No session exists yet.
          if (user.challengeName) {
            throw new Error(`Unsupported Cognito challenge: ${user.challengeName}`)
          }

          try {
            await handleCart()
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Cart merge failed', error)
          }

          await navigateAfterLogin()
        })
        .catch((error) => {
          const code: string = error?.code ?? error?.name ?? ''

          // User must set a new password first: continue on the forgot password page.
          // The email is passed as masked query so it does not show up in the visible URL.
          if (code === COGNITO_ERROR_PASSWORD_RESET_REQUIRED) {
            const forgotPasswordPath = ROUTER_PATH.FORGOT_PASSWORD[defaultLocale]
            push({ pathname: forgotPasswordPath, query: { email, reset_required: 'true' } }, forgotPasswordPath, {
              locale
            })
            return
          }

          setFormErrorCode(code)
          setFormError(getSignInErrorMessage(code, error?.message ?? ''))
          if (code === COGNITO_ERROR_USER_NOT_CONFIRMED) {
            setUnconfirmedEmail(email)
          }
        })
        .finally(() => {
          setIsLoadingForm(false)
        })
    },
    [
      user,
      t,
      push,
      navigateAfterLogin,
      handleCart,
      getSignInErrorMessage,
      translateCognitoErrorMesage,
      isForceNewPassword,
      defaultLocale,
      locale
    ]
  )

  // Ignore further submits (double click, Enter) while a sign-in is running. Amplify throws
  // synchronously on a parallel signIn and completeNewPassword would send a second request.
  const isSubmittingRef = useRef(false)
  const handleSubmitFormOnce = useCallback(
    async (data: LoginFormSchema) => {
      if (isSubmittingRef.current) return
      isSubmittingRef.current = true
      try {
        await handleSubmitForm(data)
      } finally {
        isSubmittingRef.current = false
      }
    },
    [handleSubmitForm]
  )

  const handleRegister = useCallback(() => routerPush(ROUTER_PATH.SIGNUP[defaultLocale]), [routerPush, defaultLocale])

  if (isUserLoading || isAuthenticated) {
    return null
  }

  return (
    <S.Content>
      <Row>
        <Col>
          <Breadcrumb pages={breadcrumb} />
          <Text as='h2' color='primary' family='heading'>
            {subtitle}
          </Text>
          <Text as='h1' size='xxlg' family='heading' weight='bold' margin='0 0 2.5rem' itemProp='name'>
            {title}
          </Text>
        </Col>
      </Row>
      <Row>
        <Col>
          {isForceNewPassword && (
            <Text as={'h2'} size={'md'}>
              <Text as={'h2'} size={'sm'}>
                {t('CHANGE_PW_TITLE')}
              </Text>
            </Text>
          )}
          <S.WrapForm>
            <S.Form>
              <ControlledForm style={{ marginBottom: '0rem' }} formMethods={formMethods}>
                <div style={{ gap: '1.5rem', display: 'grid' }}>
                  <ControlledInput
                    name='email'
                    disabled={isForceNewPassword}
                    dimension='large'
                    isBlock
                    errorColor={errorColor}
                    label={t('LOGIN_EMAIL')}
                    type={'text'}
                  />
                  <ControlledInput
                    name='password'
                    disabled={isForceNewPassword}
                    dimension='large'
                    isBlock
                    errorColor={errorColor}
                    label={t('LOGIN_PASSWORD')}
                    type={showPassword ? 'text' : 'password'}
                    rightElement={
                      <S.ShowPasswordButton
                        type='button'
                        disabled={isForceNewPassword}
                        aria-label={showPassword ? t('HIDE_PASSWORD') : t('SHOW_PASSWORD')}
                        aria-pressed={showPassword}
                        onClick={() => setShowPassword((oldState) => !oldState)}
                      >
                        <Icon name='eye' size='s5' color='#808891' />
                      </S.ShowPasswordButton>
                    }
                  />

                  {isForceNewPassword && (
                    <>
                      <ControlledInput
                        name='new_password'
                        dimension='large'
                        isBlock
                        errorColor={errorColor}
                        label={t('CHANGE_PW_NEW_PW') + '*'}
                        type={showNewPassword ? 'text' : 'password'}
                        rightElement={
                          <S.ShowPasswordButton
                            type='button'
                            aria-label={showNewPassword ? t('HIDE_PASSWORD') : t('SHOW_PASSWORD')}
                            aria-pressed={showNewPassword}
                            onClick={() => setNewShowPassword((oldState) => !oldState)}
                          >
                            <Icon name='eye' size='s5' color='#808891' />
                          </S.ShowPasswordButton>
                        }
                      />

                      <ControlledInput
                        name='confirm_password'
                        dimension='large'
                        isBlock
                        errorColor={errorColor}
                        label={t('CHANGE_PW_REPEAT_PW') + '*'}
                        type={showNewPasswordRepeat ? 'text' : 'password'}
                        rightElement={
                          <S.ShowPasswordButton
                            type='button'
                            aria-label={showNewPasswordRepeat ? t('HIDE_PASSWORD') : t('SHOW_PASSWORD')}
                            aria-pressed={showNewPasswordRepeat}
                            onClick={() => setNewShowPasswordRepeat((oldState) => !oldState)}
                          >
                            <Icon name='eye' size='s5' color='#808891' />
                          </S.ShowPasswordButton>
                        }
                      />
                    </>
                  )}
                  <S.FormFooter>
                    {isForceNewPassword && (
                      <Button
                        kind='primary'
                        type='submit'
                        isLoading={isLoadingForm || isSubmitting}
                        isDisabled={!isDirty || !isValid}
                        onClick={formMethods.handleSubmit(handleSubmitFormOnce)}
                      >
                        {t('CHANGE_PW_CHANGE')}
                      </Button>
                    )}
                    {!isForceNewPassword && (
                      <Button
                        kind='primary'
                        type='submit'
                        isLoading={isLoadingForm || isSubmitting}
                        isDisabled={!isDirty || !isValid}
                        onClick={formMethods.handleSubmit(handleSubmitFormOnce)}
                      >
                        {t('LOGIN_LOGIN')}
                      </Button>
                    )}
                    <Link href={ROUTER_PATH.FORGOT_PASSWORD[defaultLocale]} passHref legacyBehavior>
                      <LinkComp kind={isLgBreakpoint ? 'inverse' : 'link'}>{t('LOGIN_FORGOT_PASSWORD')}</LinkComp>
                    </Link>
                  </S.FormFooter>
                  {formError && (
                    <Text color={errorColor}>
                      {formError}
                      {formErrorCode === COGNITO_ERROR_USER_NOT_CONFIRMED && (
                        <>
                          {' '}
                          <S.LinkButton
                            type='button'
                            $isInverse={isLgBreakpoint}
                            disabled={resendState === 'sending' || resendState === 'sent'}
                            onClick={handleResendConfirmation}
                          >
                            {t('LOGIN_RESEND_CONFIRMATION')}
                          </S.LinkButton>
                        </>
                      )}
                    </Text>
                  )}
                  {formErrorCode === COGNITO_ERROR_USER_NOT_CONFIRMED && resendState === 'sent' && (
                    <Text color={successColor}>{t('LOGIN_RESEND_CONFIRMATION_SUCCESS')}</Text>
                  )}
                  {formErrorCode === COGNITO_ERROR_USER_NOT_CONFIRMED && resendState === 'error' && (
                    <Text color={errorColor}>{t('LOGIN_RESEND_CONFIRMATION_ERROR')}</Text>
                  )}
                </div>
              </ControlledForm>
            </S.Form>
            <S.Aside>
              <Text as='h3' family='heading' size='lg' weight='bold' margin='0 0 1.5rem'>
                {aside?.aside_title}
              </Text>
              <Text color='weak' size='sm'>
                {aside?.aside_description}
              </Text>

              <Button kind='weak' isOutlined onClick={handleRegister}>
                {aside?.aside_label_button}
              </Button>
            </S.Aside>
          </S.WrapForm>
        </Col>
      </Row>
    </S.Content>
  )
}
